import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Alert,
  Box,
  Typography,
  Chip,
  Paper,
  CircularProgress,
} from '@mui/material';
import { useApiClient } from '../../config/axios';
import VariantPickerModal, { VariantChoice } from './VariantPickerModal';

interface NavigationRow {
  topNav: string;
  leftNav: string;
  viewLabel: string;
  configName: string;
  viewKey: string;
}

interface TemplateRow {
  id: string;
  name: string;
  type: string;
  viewItemCount: number;
  spec?: Record<string, any>;
}

interface NewViewDialogProps {
  open: boolean;
  topNav: string;
  onClose: () => void;
  onCreated: (viewKey: string) => void;
}

const VIEW_TYPE_OPTIONS = [
  'GridView',
  'NestedGridView',
  'ListView',
  'WorklistView',
  'PivotView',
  'OverviewView',
];

function toKebab(s: string): string {
  return s.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
}

const NewViewDialog: React.FC<NewViewDialogProps> = ({ open, topNav, onClose, onCreated }) => {
  const apiClient = useApiClient();

  const [viewName, setViewName] = useState('');
  const [viewLabel, setViewLabel] = useState('');
  const [leftNav, setLeftNav] = useState('');
  const [configName, setConfigName] = useState('');
  const [viewType, setViewType] = useState('GridView');
  const [fromType, setFromType] = useState<'blank' | 'clone' | 'template'>('blank');
  const [sourceViewKey, setSourceViewKey] = useState('');

  // Template picker state
  const [templateSearch, setTemplateSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [templateType, setTemplateType] = useState<string>('all');
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  const [navRows, setNavRows] = useState<NavigationRow[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Variant picker queue (for template path)
  const [variantQueue, setVariantQueue] = useState<string[]>([]);
  const [collectedVariantChoices, setCollectedVariantChoices] = useState<Record<string, VariantChoice>>({});
  const [variantProgressTotal, setVariantProgressTotal] = useState(0);

  // Reset state on open
  useEffect(() => {
    if (!open) return;
    setViewName('');
    setViewLabel('');
    setLeftNav('');
    setConfigName('');
    setViewType('GridView');
    setFromType('blank');
    setSourceViewKey('');
    setTemplateSearch('');
    setDebouncedSearch('');
    setTemplateType('all');
    setSelectedTemplateId('');
    setError(null);
    setVariantQueue([]);
    setCollectedVariantChoices({});
    setVariantProgressTotal(0);
  }, [open]);

  // Debounce template search (300ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(templateSearch), 300);
    return () => clearTimeout(t);
  }, [templateSearch]);

  // Auto-suggest configName from viewName
  useEffect(() => {
    if (viewName && !configName) {
      setConfigName(toKebab(viewName));
    }
  }, [viewName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load navigation rows for left-nav autocomplete + clone source list
  useEffect(() => {
    if (!open) return;
    apiClient
      .get('/navigation')
      .then((r) => setNavRows(r.data || []))
      .catch((e) => console.error('Failed to load navigation', e));
  }, [open, apiClient]);

  // Load templates when picker is open
  useEffect(() => {
    if (!open || fromType !== 'template') return;
    setTemplatesLoading(true);
    const params: Record<string, string> = { limit: '100' };
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    if (templateType !== 'all') params.type = templateType;
    apiClient
      .get('/templates', { params })
      .then((r) => setTemplates(r.data || []))
      .catch((e) => {
        console.error('Failed to load templates', e);
        setTemplates([]);
      })
      .finally(() => setTemplatesLoading(false));
  }, [open, fromType, debouncedSearch, templateType, apiClient]);

  const sectionRows = useMemo(
    () => navRows.filter((r) => r.topNav === topNav),
    [navRows, topNav]
  );

  const leftNavOptions = useMemo(
    () => Array.from(new Set(sectionRows.map((r) => r.leftNav))).filter(Boolean),
    [sectionRows]
  );

  const cloneSourceOptions = useMemo(
    () =>
      sectionRows.map((r) => ({
        value: r.viewKey,
        label: `${r.viewLabel} (${r.viewKey})`,
      })),
    [sectionRows]
  );

  const templateTypeOptions = useMemo(() => {
    const types = new Set<string>();
    templates.forEach((t) => t.type && types.add(t.type));
    return Array.from(types).sort();
  }, [templates]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );

  // When the planner picks a template, default-fill view_type if blank
  useEffect(() => {
    if (selectedTemplate && fromType === 'template' && !viewType) {
      setViewType(selectedTemplate.type || 'GridView');
    }
  }, [selectedTemplate, fromType]); // eslint-disable-line react-hooks/exhaustive-deps

  const canSubmit =
    viewName.trim() &&
    viewLabel.trim() &&
    leftNav.trim() &&
    configName.trim() &&
    topNav &&
    (fromType === 'blank' ? !!viewType :
     fromType === 'clone' ? !!sourceViewKey && !!viewType :
     fromType === 'template' ? !!selectedTemplateId :
     false);

  // Build the POST body. variant_choices is included for the template path.
  const buildBody = (variantChoices: Record<string, VariantChoice>) => {
    const fromObj: any =
      fromType === 'blank' ? { type: 'blank' } :
      fromType === 'clone' ? { type: 'clone', source_view_key: sourceViewKey } :
      { type: 'template', template_id: selectedTemplateId };

    const body: any = {
      view_name: viewName.trim(),
      view_label: viewLabel.trim(),
      top_nav: topNav,
      left_nav: leftNav.trim(),
      config_name: configName.trim(),
      from: fromObj,
    };
    if (viewType) body.view_type = viewType;
    if (fromType === 'template') body.variant_choices = variantChoices;
    return body;
  };

  // Try POST. On 400 needs_variant, queue picker; the queue handler retries.
  const submitWith = async (variantChoices: Record<string, VariantChoice>) => {
    setError(null);
    setSubmitting(true);
    try {
      const res = await apiClient.post('/view-definition', buildBody(variantChoices));
      setSubmitting(false);
      onCreated(res.data.view_key);
    } catch (e: any) {
      setSubmitting(false);
      const status = e?.response?.status;
      const data = e?.response?.data;
      if (status === 400 && Array.isArray(data?.needs_variant) && data.needs_variant.length > 0) {
        // Open variant picker queue. Already-collected choices stay; only
        // newly-missing data_indexes get queued.
        const missing = data.needs_variant.filter((di: string) => !variantChoices[di]);
        setVariantQueue(missing);
        setVariantProgressTotal(missing.length);
        return;
      }
      const msg =
        status === 409
          ? `View name "${viewName}" already exists`
          : data?.error || e?.message || 'Failed to create view';
      setError(msg);
    }
  };

  const handleSubmit = () => submitWith(collectedVariantChoices);

  // Variant picker callbacks
  const handleVariantChosen = (choice: VariantChoice) => {
    if (variantQueue.length === 0) return;
    const di = variantQueue[0];
    const updated = { ...collectedVariantChoices, [di]: choice };
    setCollectedVariantChoices(updated);
    const remaining = variantQueue.slice(1);
    setVariantQueue(remaining);
    if (remaining.length === 0) {
      // Queue empty — retry the create with all collected choices.
      submitWith(updated);
    }
  };

  const handleVariantCancel = () => {
    // Abort the whole flow. Backend already rolled back on the 400.
    setVariantQueue([]);
    setVariantProgressTotal(0);
    setCollectedVariantChoices({});
    setError('Cancelled — no view was created.');
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth={fromType === 'template' ? 'lg' : 'sm'} fullWidth>
        <DialogTitle>New View in {topNav}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            {variantQueue.length > 0 && (
              <Alert severity="info">
                Resolving {variantProgressTotal - variantQueue.length + 1} of {variantProgressTotal} metric definition{variantProgressTotal === 1 ? '' : 's'} for this template…
              </Alert>
            )}

            <TextField
              label="View Name"
              value={viewName}
              onChange={(e) => setViewName(e.target.value)}
              required
              fullWidth
              size="small"
              helperText="Unique identifier (also used as the database key)"
            />

            <TextField
              label="View Label"
              value={viewLabel}
              onChange={(e) => setViewLabel(e.target.value)}
              required
              fullWidth
              size="small"
              helperText="Display name shown in the sidebar"
            />

            <Autocomplete
              freeSolo
              options={leftNavOptions}
              value={leftNav}
              onChange={(_, v) => setLeftNav(v || '')}
              onInputChange={(_, v) => setLeftNav(v)}
              renderInput={(params) => (
                <TextField {...params} label="Left Nav Section" required size="small" />
              )}
            />

            <TextField
              label="Config Name"
              value={configName}
              onChange={(e) => setConfigName(e.target.value)}
              required
              fullWidth
              size="small"
              helperText="Auto-suggested from view name"
            />

            <FormControl fullWidth size="small" required={fromType !== 'template'}>
              <InputLabel>View Type {fromType === 'template' ? '(optional — defaults from template)' : ''}</InputLabel>
              <Select
                label={`View Type ${fromType === 'template' ? '(optional — defaults from template)' : ''}`}
                value={viewType}
                onChange={(e) => setViewType(e.target.value)}
              >
                {VIEW_TYPE_OPTIONS.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
                {selectedTemplate?.type && !VIEW_TYPE_OPTIONS.includes(selectedTemplate.type) && (
                  <MenuItem key={selectedTemplate.type} value={selectedTemplate.type}>
                    {selectedTemplate.type} (from template)
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <FormControl>
              <RadioGroup
                row
                value={fromType}
                onChange={(e) => setFromType(e.target.value as 'blank' | 'clone' | 'template')}
              >
                <FormControlLabel value="blank" control={<Radio size="small" />} label="Blank" />
                <FormControlLabel value="clone" control={<Radio size="small" />} label="Clone existing" />
                <FormControlLabel value="template" control={<Radio size="small" />} label="From template" />
              </RadioGroup>
            </FormControl>

            {fromType === 'clone' && (
              <FormControl fullWidth size="small" required>
                <InputLabel>Source View</InputLabel>
                <Select
                  label="Source View"
                  value={sourceViewKey}
                  onChange={(e) => setSourceViewKey(e.target.value)}
                >
                  {cloneSourceOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {fromType === 'template' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <TextField
                    label="Search templates"
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      label="Type"
                      value={templateType}
                      onChange={(e) => setTemplateType(e.target.value)}
                    >
                      <MenuItem value="all">All types</MenuItem>
                      {templateTypeOptions.map((t) => (
                        <MenuItem key={t} value={t}>{t}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {templatesLoading ? 'Loading…' : `${templates.length} template${templates.length === 1 ? '' : 's'}`}
                  </Typography>
                </Box>

                {templatesLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={20} />
                  </Box>
                )}

                {!templatesLoading && templates.length === 0 && (
                  <Alert severity="info">No templates match this search.</Alert>
                )}

                {!templatesLoading && templates.length > 0 && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                      gap: 1.5,
                      maxHeight: 420,
                      overflowY: 'auto',
                      p: 0.5,
                    }}
                  >
                    {templates.map((t) => {
                      const selected = t.id === selectedTemplateId;
                      return (
                        <Paper
                          key={t.id}
                          onClick={() => setSelectedTemplateId(t.id)}
                          elevation={selected ? 4 : 1}
                          sx={{
                            p: 1.5,
                            cursor: 'pointer',
                            border: selected ? '2px solid' : '1px solid transparent',
                            borderColor: selected ? 'primary.main' : 'divider',
                            backgroundColor: selected ? 'action.selected' : 'background.paper',
                            transition: 'border-color 0.15s, box-shadow 0.15s',
                            '&:hover': { borderColor: 'primary.light' },
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                            <Typography variant="subtitle2" sx={{ flex: 1, wordBreak: 'break-word' }}>
                              {t.name}
                            </Typography>
                            <Chip label={t.type || 'no_type'} size="small" />
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {t.viewItemCount} item{t.viewItemCount === 1 ? '' : 's'}
                          </Typography>
                        </Paper>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit || submitting}>
            {submitting ? 'Creating…' : 'Create View'}
          </Button>
        </DialogActions>
      </Dialog>

      {variantQueue.length > 0 && (
        <VariantPickerModal
          open
          dataIndex={variantQueue[0]}
          prdCalculation={null}
          onChoose={handleVariantChosen}
          onCancel={handleVariantCancel}
        />
      )}
    </>
  );
};

export default NewViewDialog;
